import React, { useContext, useEffect, useState } from 'react';
import {
	Box,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControlLabel,
	Grid,
	Link,
	Typography,
	useTheme,
} from '@mui/material';
import MainButton from '../../UI/MainButton';
import isEmail from 'validator/lib/isEmail';
import SnackbarContext from '../../../store/snackbar-context';
import FormTextField from '../../UI/FormTextField';
import FormDropdown from '../../UI/FormDropdown';
import FormComboBox from '../../UI/FormComboBox';
import { getDistricts, getSchools } from '../../../api/user-api';
import { useNavigate } from 'react-router-dom';
import DividerWithButton from '../../UI/DividerWithButton';
import { loginPage, studentsPage } from '../../../store/fixed-routing';
import {
	registerNewTeacher,
	checkPriorTeacher,
	verifyPriorTeacher,
	checkDuplicateName,
	DuplicateNameWarning,
} from '../../../api/auth-api';
import TextWithDivider from '../../UI/TextWithDivider';
import { debugError } from '../../../utils/debug';
import { formatPersonName } from '../../../utils/person-name-case';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
	DeclaredStudentRow,
	DECLARED_STUDENTS_MAX_CHARS,
	emptyDeclaredStudent,
	parseDeclaredStudentsForSummary,
	serializeDeclaredStudents,
	validateDeclaredStudents,
} from '../../../utils/declared-students';
import { applyTeacherPrefill, TeacherPrefill } from '../../../utils/teacher-prefill';
import DeclaredStudentsEditor from './DeclaredStudentsEditor';
import {
	getTeacherCategoryOptions,
	getTeacherCategoryDropdownValues,
	SCIENTIFIC_SUPERVISOR_CATEGORY,
	categoryImpliesNoSchool,
	isScientificOnlyCategory,
	showNoSchoolFacilityCheckbox,
} from '../../../utils/teacher-registration-categories';
import { getVariantCapability, isFiloOlympicVariant } from '../../../config/variant-profile';
import { expectedGuardianDistrictName, guardianDistrictValidation } from '../../../utils/teacher-district';
import { sendResetLinkRequest } from '../../../api/auth-api';
import authContext from '../../../store/auth-context';

import {
	computeTeacherRegisterValidation,
	emptyTeacherRegisterValidation,
	teacherRegisterErrorMessages,
} from '../../../utils/teacher-register-validation';

const RegisterTeacher = ({
	prefill,
	resumeMode = false,
}: {
	prefill?: TeacherPrefill | null;
	resumeMode?: boolean;
}) => {
	const navigate = useNavigate();
	const { palette, typography } = useTheme();
	const [districts, setDistricts] = useState<any>();
	const [schools, setSchools] = useState<any>();
	const [validated, setValidated] = useState(false);
	const { setMsg } = useContext(SnackbarContext);
	const { email: loggedInEmail, token, establishSession } = useContext(authContext);
	const categoryOptions = getTeacherCategoryOptions();
	const [formInfo, setFormInfo] = useState({
		personalTitle: '',
		academicDegree: '',
		firstName: '',
		secondName: '',
		lastName: '',
		category: '',
		affiliation: '',
		schoolRspo: '',
		students: '',
		email: '',
		phone: '',
		city: '',
		street: '',
		zipCode: '',
		building: '',
		apartment: '',
		districtId: '',
		password: '',
		passwordConfirm: '',
		noSchoolFacility: false,
	});
	const [isSecondName, setIsSecondName] = useState(false);
	const [skipPassword, setSkipPassword] = useState(false);
	const [priorMatchHint, setPriorMatchHint] = useState<string | null>(null);
	const [priorRequiresPassword, setPriorRequiresPassword] = useState(false);
	const [priorVerified, setPriorVerified] = useState(false);
	const [priorVerifyPassword, setPriorVerifyPassword] = useState('');
	const [priorVerifyLoading, setPriorVerifyLoading] = useState(false);
	const [priorPasswordDialogOpen, setPriorPasswordDialogOpen] = useState(false);
	const [priorResetSent, setPriorResetSent] = useState(false);
	const [priorResetLoading, setPriorResetLoading] = useState(false);
	const [schoolFromCatalog, setSchoolFromCatalog] = useState(false);
	const [districtsDisabled, setDistrictsDisabled] = useState(false);
	const [nameWarnings, setNameWarnings] = useState<DuplicateNameWarning[]>([]);
	const [declaredStudents, setDeclaredStudents] = useState<DeclaredStudentRow[]>([emptyDeclaredStudent()]);

	const [helperInputInfo, setHelperInputInfo] = useState({
		district: '',
		affiliation: '',
		rodo: '',
		consent: '',
	});

	const [districtNames, setDistrictNames] = useState<any>([]);
	const [schoolNames, setSchoolNames] = useState<any>([]);

	const [categoryNames] = useState<string[]>(getTeacherCategoryDropdownValues());

	useEffect(() => {
		const fetchData = async () => {
			const districts = await getDistricts();
			const schools = await getSchools();
			setDistricts(districts);
			setSchools(schools);

			if (districts) {
				setDistrictNames(districts.map((district: any) => district.name));
			}

			if (schools) {
				const schoolList = schools
					.sort((a: any, b: any) => a.city.localeCompare(b.city))
					.map((school: any) => school.city.toUpperCase() + ', ' + school.name.toUpperCase());
				setSchoolNames(Array.from(new Set(schoolList)));
			}
		};
		fetchData().catch((error) => debugError('RegisterTeacher fetchData error:', error));
	}, []);

	useEffect(() => {
		if (!prefill || !districts) {
			return;
		}
		const result = applyTeacherPrefill(prefill, districts, schools, setFormInfo, setHelperInputInfo, setIsSecondName);
		setSchoolFromCatalog(result.schoolFromCatalog);
		setDistrictsDisabled(result.schoolFromCatalog);
		const emailOverride = resumeMode && loggedInEmail ? { email: loggedInEmail } : {};
		syncValidationAfterPrefill(result, emailOverride);
		if (resumeMode && loggedInEmail) {
			setFormInfo((prev) => ({ ...prev, email: loggedInEmail }));
		}
		if (resumeMode) {
			setSkipPassword(true);
			setPriorVerified(true);
		}
	}, [prefill, districts, schools, resumeMode, loggedInEmail]);

	const refreshNameWarnings = async (names?: { firstName?: string; lastName?: string }) => {
		const firstName = formatPersonName(names?.firstName ?? formInfo.firstName);
		const lastName = formatPersonName(names?.lastName ?? formInfo.lastName);
		if (!firstName || !lastName || !formInfo.districtId) {
			setNameWarnings([]);
			return;
		}
		try {
			const result = await checkDuplicateName({
				firstName,
				lastName,
				districtId: formInfo.districtId,
				excludeEmail: formInfo.email || undefined,
				role: 'both',
			});
			setNameWarnings(result?.warnings ?? []);
		} catch (error) {
			debugError('checkDuplicateName failed', error);
		}
	};

	const normalizeTeacherNameFields = () => {
		setFormInfo((prev) => ({
			...prev,
			firstName: formatPersonName(prev.firstName),
			secondName: isSecondName ? formatPersonName(prev.secondName) : prev.secondName,
			lastName: formatPersonName(prev.lastName),
			...(prev.noSchoolFacility ? { city: formatPersonName(prev.city) } : {}),
		}));
	};

	const firstNameBlur = () => {
		const firstName = formatPersonName(formInfo.firstName);
		const lastName = formatPersonName(formInfo.lastName);
		if (firstName !== formInfo.firstName) {
			setFormInfo((prev) => ({ ...prev, firstName }));
		}
		void refreshNameWarnings({ firstName, lastName });
	};

	const lastNameBlur = () => {
		const firstName = formatPersonName(formInfo.firstName);
		const lastName = formatPersonName(formInfo.lastName);
		if (lastName !== formInfo.lastName) {
			setFormInfo((prev) => ({ ...prev, lastName }));
		}
		void refreshNameWarnings({ firstName, lastName });
	};

	const secondNameBlur = () => {
		const secondName = formatPersonName(formInfo.secondName);
		if (secondName !== formInfo.secondName) {
			setFormInfo((prev) => ({ ...prev, secondName }));
		}
	};

	const cityBlur = () => {
		if (!formInfo.noSchoolFacility) {
			return;
		}
		const city = formatPersonName(formInfo.city);
		if (city !== formInfo.city) {
			setFormInfo((prev) => ({ ...prev, city }));
		}
		syncGuardianDistrict(city, formInfo.zipCode);
		refreshGuardianDistrictValidation(city, formInfo.zipCode, helperInputInfo.district);
	};

	const checkEmailForPriorProfile = async () => {
		if (resumeMode) {
			return;
		}
		if (!formInfo.email || !formInfo.firstName || !formInfo.lastName) {
			return;
		}
		try {
			const result = await checkPriorTeacher(
				formInfo.email,
				formInfo.firstName,
				formInfo.lastName,
				formInfo.districtId || undefined,
			);
			if (result?.match) {
				if (result.requiresPassword) {
					setPriorMatchHint(
						'Rozpoznaliśmy konto z poprzedniej edycji — potwierdź hasło, aby wczytać dane kontaktowe na bieżącą edycję.',
					);
					setPriorRequiresPassword(true);
					setPriorVerified(false);
					setSkipPassword(false);
					setPriorVerifyPassword('');
					setPriorPasswordDialogOpen(true);
				} else {
					setPriorMatchHint('Rozpoznaliśmy profil z poprzedniej edycji — uzupełnij dane na bieżącą edycję.');
					setPriorRequiresPassword(false);
					setPriorVerified(true);
					setSkipPassword(true);
					if (result.prefill && districts) {
						const applied = applyTeacherPrefill(
							result.prefill,
							districts,
							schools,
							setFormInfo,
							setHelperInputInfo,
							setIsSecondName,
						);
						setSchoolFromCatalog(applied.schoolFromCatalog);
						setDistrictsDisabled(applied.schoolFromCatalog);
						syncValidationAfterPrefill(applied, {
							email: formInfo.email,
							firstName: formInfo.firstName || applied.appliedForm.firstName,
							lastName: formInfo.lastName || applied.appliedForm.lastName,
						});
					}
				}
				setNameWarnings(result.nameWarnings ?? []);
			} else {
				setPriorMatchHint(null);
				setPriorRequiresPassword(false);
				setPriorVerified(false);
				setSkipPassword(false);
				setPriorPasswordDialogOpen(false);
				await refreshNameWarnings();
			}
		} catch (error) {
			debugError('checkPriorTeacher failed', error);
		}
	};

	const syncGuardianDistrict = (city: string, zipCode: string) => {
		if (!formInfo.noSchoolFacility || !districts?.length) {
			return;
		}
		const expected = expectedGuardianDistrictName(city, zipCode, districtNames);
		if (!expected) {
			return;
		}
		const district = districts.find((d: any) => d.name === expected);
		if (!district) {
			return;
		}
		setFormInfo((prev) => ({ ...prev, districtId: String(district.id) }));
		setHelperInputInfo((prev) => ({ ...prev, district: expected }));
		setFormValidation((prev) => ({ ...prev, districtError: '' }));
	};

	const refreshGuardianDistrictValidation = (city: string, zipCode: string, districtName: string) => {
		if (!formInfo.noSchoolFacility || !districtName) {
			return;
		}
		const districtValidationError = guardianDistrictValidation(districtName, city, zipCode, districtNames);
		setFormValidation((prev) => ({
			...prev,
			districtError: districtValidationError || '',
		}));
	};

	const requestPriorPasswordReset = async () => {
		if (!formInfo.email) {
			setMsg({ msg: 'Podaj adres e-mail w formularzu.', severity: 'warning' });
			return;
		}
		setPriorResetLoading(true);
		try {
			const result = await sendResetLinkRequest(formInfo.email, (error) => {
				setMsg({ msg: error, severity: 'error' });
			});
			if (result?.user_exists === false) {
				setMsg({ msg: result.message || 'Nie znaleziono konta dla tego adresu e-mail.', severity: 'warning' });
				return;
			}
			setPriorResetSent(true);
			setMsg({
				msg: 'Wysłaliśmy link do resetu hasła. Ustaw nowe hasło z wiadomości, wróć tutaj i wpisz je w oknie poniżej.',
				severity: 'success',
			});
		} catch (error) {
			debugError('requestPriorPasswordReset failed', error);
		} finally {
			setPriorResetLoading(false);
		}
	};

	const confirmPriorPassword = async () => {
		if (!formInfo.email || !priorVerifyPassword) {
			setMsg({ msg: 'Podaj hasło do istniejącego konta.', severity: 'warning' });
			return;
		}
		setPriorVerifyLoading(true);
		try {
			const result = await verifyPriorTeacher(
				formInfo.email,
				priorVerifyPassword,
				formInfo.firstName,
				formInfo.lastName,
			);
			if (!result?.ok) {
				setMsg({ msg: result?.error || 'Nieprawidłowe hasło.', severity: 'error' });
				return;
			}
			setPriorVerified(true);
			setPriorRequiresPassword(false);
			setSkipPassword(true);
			setPriorPasswordDialogOpen(false);
			setPriorMatchHint('Dane z poprzedniej edycji zostały wczytane — wybierz placówkę i uczniów na bieżącą edycję.');
			if (result.prefill && districts) {
				const applied = applyTeacherPrefill(
					result.prefill,
					districts,
					schools,
					setFormInfo,
					setHelperInputInfo,
					setIsSecondName,
				);
				setSchoolFromCatalog(applied.schoolFromCatalog);
				setDistrictsDisabled(applied.schoolFromCatalog);
				syncValidationAfterPrefill(applied, {
					email: formInfo.email,
					firstName: formInfo.firstName || applied.appliedForm.firstName,
					lastName: formInfo.lastName || applied.appliedForm.lastName,
				});
			}
		} finally {
			setPriorVerifyLoading(false);
		}
	};

	const [formValidation, setFormValidation] = useState(emptyTeacherRegisterValidation);

	const syncValidationAfterPrefill = (
		applied: ReturnType<typeof applyTeacherPrefill>,
		formOverrides: Partial<typeof formInfo> = {},
	) => {
		const mergedForm = { ...applied.appliedForm, ...formOverrides };
		const mergedHelper = {
			...applied.appliedHelper,
			rodo: helperInputInfo.rodo,
			consent: helperInputInfo.consent,
		};
		setFormValidation(
			computeTeacherRegisterValidation({
				formInfo: mergedForm,
				helperInputInfo: mergedHelper,
				isSecondName: applied.hasSecondName,
				schoolFromCatalog: applied.schoolFromCatalog,
				declaredStudents,
				districtNames,
			}),
		);
		setValidated(false);
	};

	const titleHandler = (value: string) => {
		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				titleError: teacherRegisterErrorMessages.titleError,
			});
		} else {
			setFormValidation({
				...formValidation,
				titleError: '',
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				personalTitle: value,
			};
		});
	};

	const degreeHandler = (value: string) => {
		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				degreeError: teacherRegisterErrorMessages.degreeError,
			});
		} else {
			setFormValidation({
				...formValidation,
				degreeError: '',
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				academicDegree: value,
			};
		});
	};

	const affiliationHandler = (e: any, value: string) => {
		const school = schools?.find(
			(s: any) => s.city.toUpperCase() + ', ' + s.name.toUpperCase() === (value || '').toUpperCase(),
		);
		if (!value || !school) {
			setFormValidation({
				...formValidation,
				affiliationError: teacherRegisterErrorMessages.affiliationError,
			});
			setSchoolFromCatalog(false);
			setDistrictsDisabled(false);
			setFormInfo((prevState) => ({
				...prevState,
				affiliation: value || '',
				schoolRspo: '',
			}));
			setHelperInputInfo((prevState) => ({
				...prevState,
				affiliation: value || '',
			}));
			return;
		}
		const district = districts?.find((d: any) => d.id === school.districtID);
		setFormValidation({
			...formValidation,
			affiliationError: '',
			streetError: '',
			buildingError: '',
			zipCodeError: '',
			cityError: '',
			districtError: district ? '' : formValidation.districtError,
		});
		setSchoolFromCatalog(true);
		setDistrictsDisabled(!!district);
		setFormInfo((prevState) => ({
			...prevState,
			affiliation: value,
			schoolRspo: String(school.rspo || ''),
			street: school.street || '',
			building: school.building || '',
			apartment: '',
			zipCode: school.zipCode || '',
			city: school.city || '',
			districtId: district ? String(district.id) : prevState.districtId,
		}));
		setHelperInputInfo((prevState) => ({
			...prevState,
			affiliation: value,
			district: district ? district.name : prevState.district,
		}));
	};

	const studentsHandler = (value: string) => {
		setFormInfo((prevState) => ({
			...prevState,
			students: value,
		}));
	};

	const syncDeclaredStudentsField = (rows: DeclaredStudentRow[]) => {
		const err = validateDeclaredStudents(rows);
		setFormValidation((prev) => ({
			...prev,
			studentsError: err || '',
		}));
		const serialized = serializeDeclaredStudents(rows);
		if (serialized.length >= DECLARED_STUDENTS_MAX_CHARS) {
			setFormValidation((prev) => ({
				...prev,
				studentsError: `Przekroczono limit zapisu listy uczniów (maks. ${DECLARED_STUDENTS_MAX_CHARS} znaków).`,
			}));
			return;
		}
		studentsHandler(serialized);
	};

	const nameHandler = (value: string) => {
		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				nameError: teacherRegisterErrorMessages.nameError,
			});
		} else {
			setFormValidation({
				...formValidation,
				nameError: '',
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				firstName: value,
			};
		});
	};

	const secondNameHandler = (value: string) => {
		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				secondNameError: teacherRegisterErrorMessages.secondNameError,
			});
		} else {
			setFormValidation({
				...formValidation,
				secondNameError: '',
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				secondName: value,
			};
		});
	};

	const surnameHandler = (value: string) => {
		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				surnameError: teacherRegisterErrorMessages.surnameError,
			});
		} else {
			setFormValidation({
				...formValidation,
				surnameError: '',
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				lastName: value,
			};
		});
	};

	const emailHandler = (value: string) => {
		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				emailError: teacherRegisterErrorMessages.emailError,
			});
		} else if (!isEmail(value)) {
			setFormValidation({
				...formValidation,
				emailError: 'Niepoprawny adres e-mail',
			});
		} else {
			setFormValidation({
				...formValidation,
				emailError: '',
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				email: value,
			};
		});
	};

	const phoneHandler = (value: string) => {
		setFormInfo((prevState) => {
			return {
				...prevState,
				phone: value,
			};
		});
	};

	const categoryHandler = (value: string) => {
		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				categoryError: teacherRegisterErrorMessages.categoryError,
			});
		} else {
			setFormValidation({
				...formValidation,
				categoryError: '',
			});
		}
		const scientificOnly = isScientificOnlyCategory(value);
		const guardianSplit = getVariantCapability('registration.guardianRolesSplit');
		// Filo: naukowy → domyślnie bez szkoły (można odznaczyć i wskazać szkołę).
		// Szkolny / szkolno-naukowy → zawsze szkoła, bez checkboxa.
		const noSchool = guardianSplit ? scientificOnly : categoryImpliesNoSchool(value) ? true : false;
		setFormInfo((prevState) => {
			return {
				...prevState,
				category: value,
				noSchoolFacility: noSchool,
				...(noSchool ? { affiliation: '', schoolRspo: '' } : {}),
			};
		});
		if (noSchool) {
			setSchoolFromCatalog(false);
			setDistrictsDisabled(false);
			setFormValidation((prev) => ({
				...prev,
				affiliationError: '',
			}));
			setHelperInputInfo((prev) => ({ ...prev, affiliation: '' }));
			syncGuardianDistrict(formInfo.city, formInfo.zipCode);
		} else if (guardianSplit && !scientificOnly) {
			setDistrictsDisabled(false);
		}
	};

	const districtHandler = (value: string) => {
		const district = districts?.find((d: any) => d.name === value);

		if (value.length < 1 || !district) {
			setFormValidation({
				...formValidation,
				districtError: teacherRegisterErrorMessages.districtError,
			});
		} else if (formInfo.noSchoolFacility) {
			const districtValidationError = guardianDistrictValidation(value, formInfo.city, formInfo.zipCode, districtNames);
			setFormValidation({
				...formValidation,
				districtError: districtValidationError || '',
			});
		} else {
			setFormValidation({
				...formValidation,
				districtError: '',
			});
		}
		if (!district) {
			return;
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				districtId: String(district.id),
			};
		});
		setHelperInputInfo((prevState) => {
			return {
				...prevState,
				district: value,
			};
		});
		if (district?.id && formInfo.firstName && formInfo.lastName) {
			checkDuplicateName({
				firstName: formInfo.firstName,
				lastName: formInfo.lastName,
				districtId: district.id,
				excludeEmail: formInfo.email || undefined,
				role: 'both',
			})
				.then((result) => setNameWarnings(result?.warnings ?? []))
				.catch((error) => debugError('checkDuplicateName failed', error));
		}
	};

	const streetHandler = (value: string) => {
		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				streetError: teacherRegisterErrorMessages.streetError,
			});
		} else {
			setFormValidation({
				...formValidation,
				streetError: '',
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				street: value,
			};
		});
	};

	const buildingHandler = (value: string) => {
		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				buildingError: teacherRegisterErrorMessages.buildingError,
			});
		} else {
			setFormValidation({
				...formValidation,
				buildingError: '',
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				building: value,
			};
		});
	};

	const apartmentHandler = (value: string) => {
		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				apartmentError: teacherRegisterErrorMessages.apartmentError,
			});
		} else {
			setFormValidation({
				...formValidation,
				apartmentError: '',
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				apartment: value,
			};
		});
	};

	const zipCodeHandler = (value: string) => {
		const numericValue = value.replace(/\D/g, '');
		const formattedValue = numericValue.replace(/(\d{2})(\d{1,})/, '$1-$2');

		if (formattedValue.length === 6) {
			setFormValidation({
				...formValidation,
				zipCodeError: '',
			});
		} else {
			setFormValidation({
				...formValidation,
				zipCodeError: teacherRegisterErrorMessages.zipCodeError,
			});
		}
		setFormInfo((prevState) => ({
			...prevState,
			zipCode: formattedValue,
		}));
		syncGuardianDistrict(formInfo.city, formattedValue);
		refreshGuardianDistrictValidation(formInfo.city, formattedValue, helperInputInfo.district);
	};

	const cityHandler = (value: string) => {
		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				cityError: teacherRegisterErrorMessages.cityError,
			});
		} else {
			setFormValidation({
				...formValidation,
				cityError: '',
			});
		}
		setFormInfo((prevState) => ({
			...prevState,
			city: value,
		}));
		syncGuardianDistrict(value, formInfo.zipCode);
		refreshGuardianDistrictValidation(value, formInfo.zipCode, helperInputInfo.district);
	};

	const rodoHandler = (e: any) => {
		if (!e.target.checked) {
			setFormValidation({
				...formValidation,
				rodoError: teacherRegisterErrorMessages.rodoError,
			});
		} else {
			setFormValidation({
				...formValidation,
				rodoError: '',
			});
		}
		setHelperInputInfo((prevState) => {
			return {
				...prevState,
				rodo: e.target.checked,
			};
		});
	};

	const consentHandler = (e: any) => {
		if (!e.target.checked) {
			setFormValidation({
				...formValidation,
				consentError: teacherRegisterErrorMessages.consentError,
			});
		} else {
			setFormValidation({
				...formValidation,
				consentError: '',
			});
		}
		setHelperInputInfo((prevState) => {
			return {
				...prevState,
				consent: e.target.checked,
			};
		});
	};

	const resetInputs = () => {
		setFormInfo((prevState) => {
			return {
				...prevState,
				personalTitle: '',
				academicDegree: '',
				firstName: '',
				secondName: '',
				lastName: '',
				affiliation: '',
				schoolRspo: '',
				category: '',
				students: '',
				email: '',
				phone: '',
				city: '',
				street: '',
				zipCode: '',
				building: '',
				apartment: '',
				districtId: '',
			};
		});
		setDeclaredStudents([emptyDeclaredStudent()]);
		setHelperInputInfo((prevState) => {
			return {
				...prevState,
				district: '',
				affiliation: '',
				rodo: '',
				consent: '',
			};
		});
		setSchoolFromCatalog(false);
		setDistrictsDisabled(false);
		setValidated(false);
		setFormValidation(emptyTeacherRegisterValidation());
	};

	const [loading, setLoading] = useState(false);
	const [summaryOpen, setSummaryOpen] = useState(false);

	const getPoleWord = (count: number) => {
		if (count === 1) return 'pole';
		const mod10 = count % 10;
		const mod100 = count % 100;
		if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) {
			return 'pola';
		}
		return 'pól';
	};

	const buildSummarySections = () => {
		const row = (label: string, value?: string | null) =>
			value && value.trim() !== '' ? { label, value: value.trim() } : null;
		const personalRows = [
			row('Tytuł', formInfo.personalTitle),
			row('Imię', formInfo.firstName),
			row('Drugie imię', formInfo.secondName),
			row('Nazwisko', formInfo.lastName),
			row('Stopień naukowy', formInfo.academicDegree),
			row('Kategoria', formInfo.category),
			row('Afiliacja', formInfo.affiliation),
			row('Uczniowie', parseDeclaredStudentsForSummary(formInfo.students, declaredStudents)),
			row('Email', formInfo.email),
			row('Telefon', formInfo.phone),
		].filter(Boolean) as { label: string; value: string }[];
		const addressRows = [
			row('Ulica', formInfo.street),
			row('Numer budynku', formInfo.building),
			row('Numer lokalu', formInfo.apartment),
			row('Kod pocztowy', formInfo.zipCode),
			row('Miasto', formInfo.city),
			row('Okręg', helperInputInfo.district),
		].filter(Boolean) as { label: string; value: string }[];
		const extraRows = [
			helperInputInfo.rodo ? { label: 'Zgoda RODO', value: 'Tak' } : null,
			helperInputInfo.consent ? { label: 'Zgodność danych ze stanem faktycznym', value: 'Tak' } : null,
		].filter(Boolean) as { label: string; value: string }[];
		return [
			{ title: 'Dane nauczyciela', rows: personalRows },
			{ title: 'Adres i okręg', rows: addressRows },
			{ title: 'Dodatkowe', rows: extraRows },
		].filter((section) => section.rows.length > 0);
	};

	const submitRegistration = () => {
		if (loading) {
			return;
		}
		setLoading(true);
		const payload = {
			...formInfo,
			firstName: formatPersonName(formInfo.firstName),
			secondName: isSecondName ? formatPersonName(formInfo.secondName) : formInfo.secondName,
			lastName: formatPersonName(formInfo.lastName),
			city: formInfo.noSchoolFacility ? formatPersonName(formInfo.city) : formInfo.city,
			districtId: String(formInfo.districtId || ''),
			declaredStudents: declaredStudents.map((row) => ({
				firstName: formatPersonName(row.firstName),
				lastName: formatPersonName(row.lastName),
			})),
			password: skipPassword ? '' : formInfo.password,
			passwordConfirm: skipPassword ? '' : formInfo.passwordConfirm,
		};
		registerNewTeacher(payload, (error: string) => debugError('RegisterTeacher registration error:', error))
			.then(async (res: any) => {
				if (res?.error) {
					setMsg({ msg: res.error, severity: 'error' });
					return;
				}
				if (res?.message) {
					setMsg({ msg: res.message, severity: 'success' });
					setSummaryOpen(false);
					if (res.needsEmailConfirmation) {
						resetInputs();
						navigate('/');
						return;
					}
					if (res.resume) {
						const email = formInfo.email;
						const resumePassword = priorVerifyPassword || '';
						if (token || resumeMode) {
							resetInputs();
							navigate(studentsPage);
							return;
						}
						if (resumePassword && email) {
							const loggedIn = await establishSession(email, resumePassword);
							resetInputs();
							navigate(loggedIn ? studentsPage : loginPage);
							return;
						}
						resetInputs();
						navigate(loginPage);
						return;
					}
					resetInputs();
				}
			})
			.catch(() => {
				setMsg({
					msg: 'Nie udało się zarejestrować konta. Prosimy spróbować ponownie później.',
					severity: 'error',
				});
			})
			.finally(() => setLoading(false));
	};

	const actionSend = () => {
		if (priorRequiresPassword && !priorVerified) {
			setPriorPasswordDialogOpen(true);
			setMsg({ msg: 'Potwierdź hasło do konta z poprzedniej edycji.', severity: 'warning' });
			return;
		}
		const validation = computeTeacherRegisterValidation({
			formInfo,
			helperInputInfo,
			isSecondName,
			schoolFromCatalog,
			declaredStudents,
			districtNames,
		});
		setFormValidation(validation);
		setValidated(true);
		if (validation.titleError !== '') {
			setMsg({ msg: validation.titleError, severity: 'warning' });
			return;
		}
		if (validation.nameError !== '') {
			setMsg({ msg: validation.nameError, severity: 'warning' });
			return;
		}
		if (isSecondName && validation.secondNameError !== '') {
			setMsg({ msg: validation.secondNameError, severity: 'warning' });
			return;
		}
		if (validation.surnameError !== '') {
			setMsg({ msg: validation.surnameError, severity: 'warning' });
			return;
		}
		if (validation.streetError !== '') {
			setMsg({ msg: validation.streetError, severity: 'warning' });
			return;
		}
		if (validation.buildingError !== '') {
			setMsg({ msg: validation.buildingError, severity: 'warning' });
			return;
		}
		if (validation.apartmentError !== '') {
			setMsg({ msg: validation.apartmentError, severity: 'warning' });
			return;
		}
		if (validation.zipCodeError !== '') {
			setMsg({ msg: validation.zipCodeError, severity: 'warning' });
			return;
		}
		if (validation.cityError !== '') {
			setMsg({ msg: validation.cityError, severity: 'warning' });
			return;
		}
		if (validation.degreeError !== '') {
			setMsg({ msg: validation.degreeError, severity: 'warning' });
			return;
		}
		if (!formInfo.category || validation.categoryError !== '') {
			setMsg({
				msg: validation.categoryError || teacherRegisterErrorMessages.categoryError,
				severity: 'warning',
			});
			return;
		}
		if (!formInfo.noSchoolFacility && !schoolFromCatalog) {
			setMsg({ msg: 'Wybierz szkołę z listy (wymagany numer RSPO).', severity: 'warning' });
			return;
		}
		if (!formInfo.noSchoolFacility && validation.affiliationError !== '') {
			setMsg({ msg: validation.affiliationError, severity: 'warning' });
			return;
		}
		if (!skipPassword) {
			if (formInfo.password.length < 8) {
				setMsg({ msg: teacherRegisterErrorMessages.passwordError, severity: 'warning' });
				return;
			}
			if (formInfo.password !== formInfo.passwordConfirm) {
				setMsg({ msg: teacherRegisterErrorMessages.passwordConfirmError, severity: 'warning' });
				return;
			}
		}
		if (validation.studentsError !== '') {
			setMsg({ msg: validation.studentsError, severity: 'warning' });
			return;
		}
		if (validation.emailError !== '') {
			setMsg({ msg: validation.emailError, severity: 'warning' });
			return;
		}
		if (validation.phoneError !== '') {
			setMsg({ msg: validation.phoneError, severity: 'warning' });
			return;
		}
		if (validation.districtError !== '') {
			setMsg({ msg: validation.districtError, severity: 'warning' });
			return;
		}
		if (validation.rodoError !== '') {
			setMsg({ msg: validation.rodoError, severity: 'warning' });
			return;
		}
		if (validation.consentError !== '') {
			setMsg({ msg: validation.consentError, severity: 'warning' });
			return;
		}
		normalizeTeacherNameFields();
		setDeclaredStudents((prev) =>
			prev.map((row) => ({
				firstName: formatPersonName(row.firstName),
				lastName: formatPersonName(row.lastName),
			})),
		);
		setSummaryOpen(true);
	};

	const summarySections = buildSummarySections();
	const sectionIconByTitle: Record<string, JSX.Element> = {
		'Dane nauczyciela': <PersonOutlineIcon fontSize='small' />,
		'Adres i okręg': <SchoolOutlinedIcon fontSize='small' />,
		Dodatkowe: <InfoOutlinedIcon fontSize='small' />,
	};

	return (
		<Grid
			width={'100%'}
			maxWidth={'650px'}
			display={'flex'}
			flexDirection={'column'}
			gap={'24px'}
		>
			<Typography
				variant={'h3'}
				fontWeight={'500'}
				color={palette.primary.main}
			>
				{isFiloOlympicVariant() ? 'Zarejestruj się - opiekun' : 'Zarejestruj się - nauczyciel'}
			</Typography>
			{resumeMode && (
				<Typography color='text.secondary'>
					Uzupełnij rejestrację na bieżącą edycję. Podpowiadamy okręg i dane kontaktowe z poprzedniej edycji — listę
					uczniów i placówkę wybierz na nowo.
				</Typography>
			)}
			{priorMatchHint && (
				<Typography
					color='primary.main'
					fontWeight={500}
				>
					{priorMatchHint}
				</Typography>
			)}
			<Grid
				component={'form'}
				display={'flex'}
				flexDirection={'column'}
			>
				<TextWithDivider
					text={'Informacje o Tobie'}
					sx={{
						mt: '16px',
						mb: '24px',
					}}
				/>
				<Box
					display={'flex'}
					flexDirection={'column'}
					alignItems={'flex-start'}
					gap={'16px'}
					width={'100%'}
					mb={'16px'}
				>
					<FormDropdown
						id={'title'}
						label={'Tytuł (Pan/Pani)'}
						validated={validated}
						errorMsg={formValidation.titleError}
						value={formInfo.personalTitle}
						values={['Pan', 'Pani']}
						valueChangeHandler={titleHandler}
					/>
					<FormTextField
						id={'name'}
						type={'text'}
						label={'Imię'}
						validated={validated}
						errorMsg={formValidation.nameError}
						value={formInfo.firstName}
						valueChangeHandler={nameHandler}
						onBlur={firstNameBlur}
					/>
					<Box
						sx={{
							'& span': {
								fontSize: typography.subtitle2.fontSize,
								fontWeight: '500',
								color: palette.primary.main,
							},
						}}
					>
						<FormControlLabel
							control={
								<Checkbox
									checked={isSecondName}
									sx={{
										borderRadius: 3,
										width: 16,
										height: 16,
										mr: '6px',
									}}
								/>
							}
							label='Czy posiadasz drugie imię?'
							onChange={(e: any) => setIsSecondName(e.target.checked)}
							sx={{
								ml: '2px',
							}}
						/>
					</Box>
					{isSecondName && (
						<FormTextField
							id={'secondName'}
							type={'text'}
							label={'Drugie imię'}
							validated={validated}
							errorMsg={formValidation.secondNameError}
							value={formInfo.secondName}
							valueChangeHandler={secondNameHandler}
							onBlur={secondNameBlur}
						/>
					)}
					<FormTextField
						id={'surname'}
						type={'text'}
						label={'Nazwisko'}
						validated={validated}
						errorMsg={formValidation.surnameError}
						value={formInfo.lastName}
						valueChangeHandler={surnameHandler}
						onBlur={lastNameBlur}
					/>
					<FormTextField
						id={'email'}
						type={'email'}
						label={'E-mail'}
						validated={validated}
						errorMsg={formValidation.emailError}
						helperText={
							'Podaj adres skrzynki, do której masz dostęp — wyślemy na niego link potwierdzający rejestrację.'
						}
						value={formInfo.email}
						valueChangeHandler={emailHandler}
						onBlur={checkEmailForPriorProfile}
					/>
					{priorMatchHint && (
						<Box
							sx={{
								px: 1.5,
								py: 1,
								borderRadius: 2,
								backgroundColor: 'rgba(0, 83, 160, 0.08)',
								border: `1px solid ${palette.primary.light}`,
							}}
						>
							<Typography
								variant='body2'
								color='primary'
							>
								{priorMatchHint}
							</Typography>
						</Box>
					)}
					{!skipPassword && !(priorRequiresPassword && !priorVerified) && (
						<>
							<FormTextField
								id={'password'}
								type={'password'}
								label={'Hasło (min. 8 znaków)'}
								validated={validated}
								errorMsg={formValidation.passwordError}
								value={formInfo.password}
								valueChangeHandler={(value: string) => setFormInfo((prev) => ({ ...prev, password: value }))}
							/>
							<FormTextField
								id={'passwordConfirm'}
								type={'password'}
								label={'Powtórz hasło'}
								validated={validated}
								errorMsg={formValidation.passwordConfirmError}
								value={formInfo.passwordConfirm}
								valueChangeHandler={(value: string) => setFormInfo((prev) => ({ ...prev, passwordConfirm: value }))}
							/>
						</>
					)}
					{nameWarnings.length > 0 && (
						<Typography
							variant='body2'
							color='warning.main'
						>
							Uwaga: w tym okręgu jest już zarejestrowana osoba o tym samym imieniu i nazwisku (
							{nameWarnings.map((w) => `${w.maskedEmail}${w.edition ? `, ed. ${w.edition}` : ''}`).join('; ')}
							). Możesz kontynuować rejestrację.
						</Typography>
					)}
					<FormTextField
						id={'degree'}
						type={'text'}
						label={'Stopień naukowy'}
						validated={validated}
						errorMsg={formValidation.degreeError}
						value={formInfo.academicDegree}
						valueChangeHandler={degreeHandler}
					/>
					<FormDropdown
						id={'category'}
						label={'Kategoria'}
						validated={validated}
						errorMsg={formValidation.categoryError}
						value={formInfo.category}
						values={categoryNames}
						valueChangeHandler={categoryHandler}
					/>
					{showNoSchoolFacilityCheckbox(formInfo.category) && (
						<FormControlLabel
							control={
								<Checkbox
									checked={formInfo.noSchoolFacility}
									onChange={(e) => {
										const checked = e.target.checked;
										setFormInfo((prev) => ({
											...prev,
											noSchoolFacility: checked,
											affiliation: checked ? '' : prev.affiliation,
											schoolRspo: checked ? '' : prev.schoolRspo,
											// OLiJP: zaznaczenie wymusza kategorię opiekuna naukowego.
											category: checked ? SCIENTIFIC_SUPERVISOR_CATEGORY : prev.category,
										}));
										setSchoolFromCatalog(false);
										setDistrictsDisabled(false);
										if (checked) {
											setFormValidation((prev) => ({
												...prev,
												affiliationError: '',
												categoryError: '',
											}));
											setHelperInputInfo((prev) => ({ ...prev, affiliation: '' }));
											syncGuardianDistrict(formInfo.city, formInfo.zipCode);
										}
									}}
								/>
							}
							label={categoryOptions.noSchoolFacilityLabel}
						/>
					)}
					{!formInfo.noSchoolFacility && (
						<FormComboBox
							id={'affiliation'}
							label={'Szkoła (wybierz z listy)'}
							validated={validated}
							errorMsg={formValidation.affiliationError}
							value={helperInputInfo.affiliation}
							values={schoolNames}
							valueChangeHandler={affiliationHandler}
						/>
					)}
					{!formInfo.noSchoolFacility && schoolFromCatalog && (
						<Box
							display={'flex'}
							flexDirection={'column'}
							width={'100%'}
							gap={'16px'}
						>
							<Typography
								variant='body2'
								fontWeight={600}
								color={palette.primary.main}
							>
								Adres szkoły (z katalogu RSPO)
							</Typography>
							<FormTextField
								id={'street'}
								type={'text'}
								label={'Ulica'}
								validated={validated}
								errorMsg={formValidation.streetError}
								value={formInfo.street}
								valueChangeHandler={streetHandler}
								disabled
							/>
							<FormTextField
								id={'building'}
								type={'text'}
								label={'Numer budynku'}
								validated={validated}
								errorMsg={formValidation.buildingError}
								value={formInfo.building}
								valueChangeHandler={buildingHandler}
								disabled
							/>
							<Box
								display={'flex'}
								gap={'16px'}
								justifyContent={'space-between'}
							>
								<FormTextField
									id={'zip'}
									type={'text'}
									label={'Kod pocztowy'}
									validated={validated}
									errorMsg={formValidation.zipCodeError}
									value={formInfo.zipCode}
									valueChangeHandler={zipCodeHandler}
									disabled
								/>
								<FormTextField
									id={'city'}
									type={'text'}
									label={'Miasto'}
									validated={validated}
									errorMsg={formValidation.cityError}
									value={formInfo.city}
									valueChangeHandler={cityHandler}
									disabled
								/>
							</Box>
						</Box>
					)}
					{formInfo.noSchoolFacility && (
						<Box
							display={'flex'}
							flexDirection={'column'}
							width={'100%'}
							gap={'16px'}
						>
							<Typography
								variant='body2'
								fontWeight={600}
								color={palette.primary.main}
							>
								Adres do korespondencji
							</Typography>
							<FormTextField
								id={'street'}
								type={'text'}
								label={'Ulica'}
								validated={validated}
								errorMsg={formValidation.streetError}
								value={formInfo.street}
								valueChangeHandler={streetHandler}
							/>
							<FormTextField
								id={'building'}
								type={'text'}
								label={'Numer budynku / lokalu'}
								validated={validated}
								errorMsg={formValidation.buildingError}
								value={formInfo.building}
								valueChangeHandler={buildingHandler}
							/>
							<FormTextField
								id={'apartment'}
								type={'text'}
								label={'Nr lokalu (opcjonalnie)'}
								validated={validated}
								errorMsg={formValidation.apartmentError}
								value={formInfo.apartment}
								valueChangeHandler={apartmentHandler}
							/>
							<Box
								display={'flex'}
								gap={'16px'}
								justifyContent={'space-between'}
							>
								<FormTextField
									id={'zip'}
									type={'text'}
									label={'Kod pocztowy'}
									validated={validated}
									errorMsg={formValidation.zipCodeError}
									value={formInfo.zipCode}
									valueChangeHandler={zipCodeHandler}
								/>
								<FormTextField
									id={'city'}
									type={'text'}
									label={'Miasto'}
									validated={validated}
									errorMsg={formValidation.cityError}
									value={formInfo.city}
									valueChangeHandler={cityHandler}
									onBlur={cityBlur}
								/>
							</Box>
						</Box>
					)}
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
						<Typography
							fontWeight={600}
							color={palette.primary.main}
						>
							Uczniowie (imię i nazwisko — osobny wiersz dla każdej osoby)
						</Typography>
						<Typography
							variant='body2'
							color='text.secondary'
						>
							Zgłoszenie ucznia przez nauczyciela nie jest równoznaczne z rejestracją ucznia. W przypadku zmiany prosimy
							o edycję listy w panelu.
						</Typography>
						<DeclaredStudentsEditor
							rows={declaredStudents}
							onChange={(rows) => {
								setDeclaredStudents(rows);
								syncDeclaredStudentsField(rows);
							}}
							validated={validated}
							studentsError={formValidation.studentsError}
						/>
					</Box>
					<FormTextField
						id={'phone'}
						type={'tel'}
						label={'Numer telefonu (opcjonalnie)'}
						validated={validated}
						errorMsg={formValidation.phoneError}
						value={formInfo.phone}
						valueChangeHandler={phoneHandler}
					/>
					<FormDropdown
						id={'district'}
						label={'Okręg'}
						validated={validated}
						errorMsg={formValidation.districtError}
						value={helperInputInfo.district}
						values={districtNames}
						valueChangeHandler={districtHandler}
						isDisabled={districtsDisabled && !formInfo.noSchoolFacility}
						disabledLabel='Okręg ustawiany automatycznie po wyborze szkoły z listy'
					/>
					<TextWithDivider
						text={'Zgody'}
						dividerColor={palette.secondary.light}
						sx={{
							mt: '32px',
						}}
					/>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: '8px',
							'& span': {
								fontSize: typography.subtitle2.fontSize,
								fontWeight: '500',
								color: palette.primary.main,
							},
						}}
					>
						<FormControlLabel
							control={
								<Checkbox
									sx={{
										borderRadius: 3,
										width: 16,
										height: 16,
										mr: '6px',
									}}
								/>
							}
							label='Zgoda RODO'
							onChange={rodoHandler}
							sx={{
								ml: '2px',
							}}
						/>
						<FormControlLabel
							control={
								<Checkbox
									sx={{
										borderRadius: 3,
										width: 16,
										height: 16,
										mr: '6px',
									}}
								/>
							}
							label='Oświadczam, że powyższe dane są zgodne ze stanem faktycznym'
							onChange={consentHandler}
							sx={{
								ml: '2px',
							}}
						/>
					</Box>
				</Box>
				<Box
					mt={'16px'}
					mb={'56px'}
					alignSelf={'center'}
					display={'flex'}
					justifyContent={'center'}
					alignItems={'center'}
					position={'relative'}
				>
					<Box alignSelf={'center'}>
						<MainButton
							title={'Zarejestruj się'}
							darkVersion
							onClick={actionSend}
							sx={{
								alignSelf: 'center',
								lineHeight: '20px',
								letterSpacing: '0.18px',
							}}
						/>
					</Box>
					<CircularProgress
						sx={{
							display: loading ? 'block' : 'none',
							position: 'absolute',
							right: '-60px',
						}}
					/>
				</Box>
			</Grid>
			{/* <DividerWithButton
				label={'Masz już konto?'}
				hrefPath={loginPage}
				buttonTitle={'Zaloguj się'}
			/>*/}
			<Dialog
				open={priorPasswordDialogOpen}
				onClose={() => {
					if (!priorVerifyLoading) {
						setPriorPasswordDialogOpen(false);
					}
				}}
				fullWidth
				maxWidth='sm'
			>
				<DialogTitle>Konto z poprzedniej edycji</DialogTitle>
				<DialogContent>
					<Typography
						variant='body2'
						sx={{ mb: 2 }}
					>
						Rozpoznaliśmy adres e-mail powiązany z kontem z wcześniejszej edycji. Podaj to samo hasło, co przy logowaniu
						— wczytamy dane kontaktowe (imię, adres korespondencyjny, telefon), aby szybciej uzupełnić rejestrację na
						bieżącą edycję. Placówkę i listę uczniów wybierzesz osobno.
					</Typography>
					{priorResetSent && (
						<Typography
							variant='body2'
							color='text.secondary'
							sx={{ mb: 2 }}
						>
							Sprawdź skrzynkę e-mail, ustaw nowe hasło z linku, wróć tutaj i wpisz je poniżej, aby kontynuować.
						</Typography>
					)}
					<FormTextField
						id='prior-password'
						type='password'
						label='Hasło do istniejącego konta'
						validated={validated}
						errorMsg=''
						value={priorVerifyPassword}
						valueChangeHandler={setPriorVerifyPassword}
					/>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
					<Link
						component='button'
						type='button'
						variant='body2'
						onClick={requestPriorPasswordReset}
						sx={{ mr: 'auto' }}
						disabled={priorResetLoading || priorVerifyLoading}
					>
						{priorResetLoading ? 'Wysyłanie…' : 'Nie pamiętam hasła'}
					</Link>
					<MainButton
						title='Anuluj'
						onClick={() => setPriorPasswordDialogOpen(false)}
						sx={{ width: 'auto', px: 3 }}
					/>
					<MainButton
						title={priorVerifyLoading ? 'Sprawdzanie…' : 'Potwierdź hasło'}
						darkVersion
						onClick={confirmPriorPassword}
						disabled={priorVerifyLoading}
						sx={{ width: 'auto', px: 3 }}
					/>
				</DialogActions>
			</Dialog>
			<Dialog
				open={summaryOpen}
				onClose={() => {
					if (!loading) {
						setSummaryOpen(false);
					}
				}}
				fullWidth
				maxWidth='md'
			>
				<DialogTitle sx={{ pb: 1 }}>
					<Box
						display='flex'
						alignItems='center'
						gap={1}
					>
						<CheckCircleOutlineIcon sx={{ color: palette.primary.main }} />
						<Typography
							variant='h5'
							color={palette.primary.main}
						>
							Sprawdź dane przed wysłaniem
						</Typography>
					</Box>
				</DialogTitle>
				<DialogContent sx={{ pt: 1 }}>
					<Box
						sx={{
							mb: 2,
							px: 1.5,
							py: 1,
							borderRadius: 2,
							backgroundColor: palette.primary.light,
							border: `1px solid ${palette.primary.main}`,
						}}
					>
						<Typography
							variant='body2'
							sx={{ color: palette.primary.main }}
						>
							Prosimy o potwierdzenie poprawności danych. W przypadku konieczności wprowadzenia zmian prosimy wybrać{' '}
							<b>Wróć do edycji</b>.
						</Typography>
					</Box>
					<Box
						display='flex'
						flexDirection='column'
						gap={2}
					>
						{summarySections.map((section, sectionIdx) => (
							<Box
								key={section.title}
								sx={{
									border: `1px solid ${palette.primary.light}`,
									borderRadius: 3,
									p: { xs: 1.5, md: 2 },
									boxShadow: '0 3px 10px rgba(0,0,0,0.06)',
									backgroundColor: '#fff',
								}}
							>
								<Box
									display='flex'
									alignItems='center'
									justifyContent='space-between'
									mb={1}
								>
									<Box
										display='flex'
										alignItems='center'
										gap={1}
									>
										<Box sx={{ color: palette.primary.main, display: 'flex', alignItems: 'center' }}>
											{sectionIconByTitle[section.title]}
										</Box>
										<Typography
											variant='h6'
											sx={{ color: palette.primary.main, fontSize: { xs: '1rem', md: '1.1rem' } }}
										>
											{section.title}
										</Typography>
									</Box>
									<Chip
										size='small'
										label={`${section.rows.length} ${getPoleWord(section.rows.length)}`}
										sx={{ color: palette.primary.main, borderColor: palette.primary.main }}
										variant='outlined'
									/>
								</Box>
								{section.rows.map((item, idx) => (
									<Grid
										key={`${section.title}-${item.label}-${idx}`}
										container
										alignItems='center'
										sx={{
											display: 'grid',
											gridTemplateColumns: { xs: '1fr', md: 'minmax(200px, 32%) 1fr' },
											py: 0.75,
											columnGap: 2,
											rowGap: 0.8,
											borderBottom: `1px dashed ${palette.primary.light}`,
											'&:last-of-type': { borderBottom: 'none' },
										}}
									>
										<Typography
											variant='body1'
											sx={{ color: palette.primary.main, fontWeight: 700, textAlign: 'left', minWidth: 'fit-content' }}
										>
											{item.label}
										</Typography>
										<Box
											sx={{
												flex: 1,
												pl: { xs: 0, md: 2 },
												ml: { xs: 0, md: 'max(12%, 100px)' },
												py: 0.4,
												px: 1.2,
												borderRadius: 1.5,
												backgroundColor: 'rgba(0, 83, 160, 0.06)',
											}}
										>
											<Typography
												variant='body1'
												sx={{ color: palette.primary.main, textAlign: 'left', wordBreak: 'break-word' }}
											>
												{item.value}
											</Typography>
										</Box>
									</Grid>
								))}
								{sectionIdx < summarySections.length - 1 && <Divider sx={{ mt: 1 }} />}
							</Box>
						))}
					</Box>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2, pt: 1, justifyContent: 'space-between' }}>
					<MainButton
						title='Wróć do edycji'
						onClick={() => setSummaryOpen(false)}
						sx={{ width: 'auto', px: 3 }}
					/>
					<MainButton
						title={loading ? 'Wysyłanie...' : 'Potwierdzam i wysyłam'}
						darkVersion
						onClick={submitRegistration}
						sx={{ width: 'auto', px: 3 }}
					/>
				</DialogActions>
			</Dialog>
		</Grid>
	);
};

export default RegisterTeacher;
